/**
 * BprEngagementsPage - /so/bpr
 *
 * Engagement list for the BPR (Business Process Review) product area:
 * table of engagements (status, company, tool, budgeted hours) plus the
 * create-engagement dialog. Mirrors the SalesDealsPage list conventions.
 */

import type { BprCreateEngagementRequest, BprEngagement, SalesCompanySummary } from '@protoss/shared';
import type { PillVariant, ResizableColumnDef } from '@protoss/ui';
import {
  Button,
  CardStackIcon,
  Dialog,
  IconSize,
  NumberInput,
  Pill,
  ResizableTable,
  Select,
  SelectItem,
  TextField,
} from '@protoss/ui';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { PageErrorState } from '../components/shared/PageErrorState';
import { AppShell, MainContentShell, type RightRailItem, useModuleChatRailItem } from '../components/shell';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { bprApi } from '../features/bpr/services/bprApi';
import { salesApi } from '../features/sales/services/salesApi';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';
import './bpr-page.css';

export function bprEngagementStatusPresentation(status: string): {
  label: string;
  variant: PillVariant;
} {
  switch (status) {
    case 'draft':
      return { label: 'Draft', variant: 'muted' };
    case 'active':
      return { label: 'Active', variant: 'success' };
    case 'synthesis':
      return { label: 'Synthesis', variant: 'accent' };
    case 'completed':
      return { label: 'Completed', variant: 'primary' };
    case 'archived':
      return { label: 'Archived', variant: 'danger' };
    default:
      return { label: status.replace(/_/g, ' '), variant: 'neutral' };
  }
}

function CreateEngagementDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (engagement: BprEngagement) => void;
}) {
  const [companies, setCompanies] = React.useState<SalesCompanySummary[]>([]);
  const [companyUuid, setCompanyUuid] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [toolKey, setToolKey] = React.useState('');
  const [budgetedHours, setBudgetedHours] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setError(null);
    void salesApi
      .listCompanies()
      .then(({ companies: loaded }) => setCompanies(loaded))
      .catch(() => setError('Failed to load companies.'));
  }, [open]);

  const canSave = companyUuid !== '' && title.trim() !== '' && toolKey.trim() !== '' && !isSaving;

  const handleCreate = async () => {
    if (!canSave) return;
    setIsSaving(true);
    setError(null);
    try {
      const input: BprCreateEngagementRequest = {
        companyUuid,
        title: title.trim(),
        toolKey: toolKey.trim(),
        ...(budgetedHours.trim() !== '' && Number(budgetedHours) > 0
          ? { budgetedHours: Number(budgetedHours) }
          : {}),
      };
      const engagement = await bprApi.createEngagement(input);
      setCompanyUuid('');
      setTitle('');
      setToolKey('');
      setBudgetedHours('');
      onCreated(engagement);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create engagement.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(next) => !isSaving && onOpenChange(next)}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content bpr-dialog">
          <Dialog.Title className="dialog-title">New BPR engagement</Dialog.Title>
          <Dialog.Description className="dialog-description">
            A paid discovery engagement reviewing a customer's business processes on a platform.
          </Dialog.Description>
          <div className="bpr-dialog__form">
            <label className="bpr-dialog__field">
              <span className="bpr-dialog__field-label">Company</span>
              <Select
                value={companyUuid || undefined}
                onValueChange={setCompanyUuid}
                placeholder="Select a company"
              >
                {companies.map((company) => (
                  <SelectItem key={company.uuid} value={company.uuid}>
                    {company.name}
                  </SelectItem>
                ))}
              </Select>
            </label>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Acme NetSuite process review"
              required
            />
            <TextField
              label="Tool / platform key"
              value={toolKey}
              onChange={(e) => setToolKey(e.target.value)}
              placeholder="e.g. oracle:netsuite"
              required
            />
            <NumberInput
              label="Budgeted hours"
              value={budgetedHours}
              onChange={(e) => setBudgetedHours(e.target.value)}
              min={0}
              placeholder="e.g. 40"
            />
            {error ? <p className="bpr-dialog__error">{error}</p> : null}
          </div>
          <div className="bpr-dialog__actions">
            <Dialog.Close asChild>
              <Button type="button" variant="ghost" size="sm" disabled={isSaving}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button type="button" size="sm" disabled={!canSave} onClick={() => void handleCreate()}>
              {isSaving ? 'Creating…' : 'Create engagement'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function BprEngagementsPage() {
  const navigate = useNavigate();
  const { workspace, isLoadingWorkspace } = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const moduleChatItem = useModuleChatRailItem();

  const [engagements, setEngagements] = React.useState<BprEngagement[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);

  const loadEngagements = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEngagements(await bprApi.listEngagements());
    } catch {
      setError('Failed to load BPR engagements.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void loadEngagements();
  }, [loadEngagements]);

  const columns = React.useMemo<ResizableColumnDef<BprEngagement>[]>(
    () => [
      {
        key: 'title',
        label: 'Engagement',
        defaultWidth: 280,
        minWidth: 140,
        render: (engagement) => <span className="bpr-table__primary">{engagement.title}</span>,
      },
      {
        key: 'company',
        label: 'Company',
        defaultWidth: 200,
        minWidth: 100,
        render: (engagement) => (
          <span className="bpr-table__secondary">{engagement.companyName ?? '-'}</span>
        ),
      },
      {
        key: 'toolKey',
        label: 'Tool',
        defaultWidth: 160,
        minWidth: 80,
        render: (engagement) => (
          <span className="bpr-table__secondary">{engagement.toolKey}</span>
        ),
      },
      {
        key: 'budgetedHours',
        label: 'Budgeted hours',
        defaultWidth: 130,
        minWidth: 80,
        render: (engagement) => (
          <span className="bpr-table__secondary">{engagement.budgetedHours ?? '-'}</span>
        ),
      },
      {
        key: 'framework',
        label: 'Framework',
        defaultWidth: 140,
        minWidth: 90,
        render: (engagement) =>
          engagement.frameworkVersionUuid ? (
            <Pill variant="accent">Pinned</Pill>
          ) : (
            <Pill variant="ghost">Not pinned</Pill>
          ),
      },
      {
        key: 'createdAt',
        label: 'Created',
        defaultWidth: 160,
        minWidth: 90,
        render: (engagement) => (
          <span className="bpr-table__date">
            {engagement.createdAt
              ? new Date(engagement.createdAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })
              : '-'}
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Status',
        defaultWidth: 140,
        minWidth: 90,
        render: (engagement) => {
          const { label, variant } = bprEngagementStatusPresentation(engagement.status);
          return <Pill variant={variant}>{label}</Pill>;
        },
      },
    ],
    []
  );

  if (isLoadingWorkspace) return null;

  const leftNav = (
    <AppNav
      selected="bpr"
      hidePrimary
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const headerActions = (
    <>
      {isWorkspaceAdmin ? (
        <Button type="button" variant="outline" size="sm" onClick={() => navigate('/so/bpr/framework')}>
          Framework admin
        </Button>
      ) : null}
      <Button type="button" variant="outline" size="sm" onClick={() => void loadEngagements()} disabled={loading}>
        Refresh
      </Button>
      <Button type="button" size="sm" onClick={() => setCreateOpen(true)}>
        New engagement
      </Button>
    </>
  );

  const rightRailItems: RightRailItem[] = moduleChatItem ? [moduleChatItem] : [];

  const main = (
    <MainContentShell title="Business Process Reviews" actions={headerActions}>
      {error ? (
        <PageErrorState message={error} onRetry={() => void loadEngagements()} />
      ) : (
        <div className="bpr-table__container bpr-engagements__list">
          <ResizableTable
            columns={columns}
            rows={engagements}
            getRowKey={(engagement) => engagement.uuid}
            aria-label="BPR engagements"
            onRowClick={(engagement) => navigate(`/so/bpr/engagements/${engagement.uuid}`)}
            loading={loading}
            emptyState={
              <div className="bpr-table__empty">
                <CardStackIcon
                  width={IconSize.xl}
                  height={IconSize.xl}
                  aria-hidden="true"
                  className="bpr-table__empty-icon"
                />
                <span>No BPR engagements yet.</span>
              </div>
            }
          />
        </div>
      )}
    </MainContentShell>
  );

  return (
    <>
      <AppShell leftNav={leftNav} main={main} rightRailItems={rightRailItems} />
      <CreateEngagementDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(engagement) => {
          setCreateOpen(false);
          navigate(`/so/bpr/engagements/${engagement.uuid}`);
        }}
      />
    </>
  );
}
